/**
 * v2 claim computation.
 *
 * v1 values each account at the best of three sources we can evidence ourselves
 * and compares that against what Raketech invoiced. v2 keeps that machinery and
 * adds Raketech's replacement workbook as a fourth source, which matters for two
 * reasons:
 *
 *   1. For accounts where their restated figure exceeds anything we could
 *      evidence, the shortfall is proved by their own admission.
 *   2. Their workbook runs to May 2026, so the Dec 2024 onward period no longer
 *      depends on holding a live Affiliation Cloud session.
 *
 * Nothing here mutates the v1 inputs; `/api/raketech/compare` is consumed
 * read-only and its own derived figures are left alone.
 */

import type {
  ClaimBridgeStep,
  ClaimInvoiceEntry,
  ClaimMonth,
  ClaimRow,
  ClaimSource,
  ClaimSummary,
  SourceCandidate,
} from '../types/raketechFinalReportV2.types';
import type { NewReconDataset, NewReconOffer } from '../types/raketechReconDiff.types';

/** Last month the original reconciliation covered. */
export const RECON_END = '2024-11';

/** Subset of `/api/raketech/compare`'s issue shape that v2 reads. */
export interface CompareIssue {
  type: string;
  accountId: string;
  username?: string;
  partner?: string;
  brand?: string;
  brands?: string[];
  offer?: string;
  geo?: string;
  currency?: string;

  invoiceRevenue?: number;
  postReconInvoiceRevenue?: number;
  invoiceMonthly?: { month: string; revenue: number; ftds?: number }[];
  invoiceEntries?: {
    label?: string;
    ftds?: number;
    revenue?: number;
    monthly?: { month: string; revenue: number; ftds?: number }[];
  }[];

  reconciliationEarnings?: number;
  reconciliationEarningsOriginal?: number;
  reconMonthly?: { month: string; earnings: number }[];

  adwstatsCommission?: number;
  adwstatsCommissionOriginal?: number;
  adwstatsCurrency?: string;
  adwstatsAccountId?: string;
  adwstatsDataSynced?: boolean;
  syncedMonthly?: { month: string; commission: number }[];

  manualCheckAmount?: number;
  partnerCommissionOriginal?: number;
}

export interface CompareResponse {
  issues: CompareIssue[];
  totals?: {
    actualInvoiced?: number;
    invoiced?: number;
    reconEarnings?: number;
    adwstatsSynced?: number;
  };
}

export interface V2Options {
  /** Ignore months after this, inclusive bound (`YYYY-MM`). */
  lastMonth: string;
  /** Apply the 5% partner margin to Raketech's gross monthly columns. */
  applyMargin: boolean;
  /** Per-account AC commission by month, keyed by Raketech account id. Only
   *  present when the user has an Affiliation Cloud session, exactly as v1.
   *  The Dec 2024 onward total is derived from this rather than passed in. */
  acMonthlyByAccountId?: Map<string, Map<string, number>>;
}

/** AC earnings for the post-reconciliation tail, from the monthly series. */
function acTailFor(
  acMonthly: Map<string, number> | undefined,
  lastMonth: string,
): number {
  if (!acMonthly) return 0;
  let total = 0;
  for (const [month, amount] of acMonthly) {
    if (month > RECON_END && month <= lastMonth) total += amount || 0;
  }
  return r2(total);
}

export interface V2Result {
  rows: ClaimRow[];
  summary: ClaimSummary;
}

const r2 = (n: number) => Math.round(n * 100) / 100;

export const monthKey = (m: string): string => {
  if (/^\d{4}-\d{2}$/.test(m)) return m;
  const slash = m.match(/^\d{2}\/(\d{2})\/(\d{4})$/);
  if (slash) return `${slash[2]}-${slash[1]}`;
  const names: Record<string, string> = {
    january: '01', february: '02', march: '03', april: '04', may: '05', june: '06',
    july: '07', august: '08', september: '09', october: '10', november: '11', december: '12',
  };
  const parts = m.trim().split(/\s+/);
  if (parts.length === 2) {
    const num = names[parts[0].toLowerCase()];
    if (num) return `${parts[1]}-${num}`;
  }
  return m;
};

/** Invoiced revenue and FTDs by month for an issue, preferring the per-entry
 *  breakdown because a single account can carry several invoice lines. */
function invoiceByMonth(iss: CompareIssue): Map<string, { revenue: number; ftds: number }> {
  const out = new Map<string, { revenue: number; ftds: number }>();
  const add = (month: string, revenue: number, ftds: number) => {
    const k = monthKey(month);
    const prev = out.get(k) || { revenue: 0, ftds: 0 };
    out.set(k, { revenue: prev.revenue + (revenue || 0), ftds: prev.ftds + (ftds || 0) });
  };
  if (iss.invoiceEntries && iss.invoiceEntries.length > 0) {
    for (const entry of iss.invoiceEntries) {
      for (const m of entry.monthly || []) add(m.month, m.revenue, m.ftds || 0);
    }
  } else {
    for (const m of iss.invoiceMonthly || []) add(m.month, m.revenue, m.ftds || 0);
  }
  return out;
}

/** Raketech's invoice lines, normalised for display in the drilldown. */
function invoiceEntriesFor(iss: CompareIssue, lastMonth: string): ClaimInvoiceEntry[] {
  const entries = iss.invoiceEntries || [];
  return entries.map((entry, i) => {
    const monthly = (entry.monthly || [])
      .map((m) => ({ month: monthKey(m.month), ftds: m.ftds || 0, revenue: m.revenue || 0 }))
      .filter((m) => m.month <= lastMonth)
      .sort((a, b) => a.month.localeCompare(b.month));
    // Prefer the in-period sum so the line agrees with the month cutoff above it.
    const revenue = monthly.length > 0
      ? r2(monthly.reduce((s, m) => s + m.revenue, 0))
      : r2(entry.revenue || 0);
    return {
      label: entry.label || `Invoice line ${i + 1}`,
      ftds: monthly.length > 0 ? monthly.reduce((s, m) => s + m.ftds, 0) : (entry.ftds || 0),
      revenue,
      monthly,
    };
  }).filter((e) => e.revenue !== 0 || e.monthly.length > 0);
}

function seriesByMonth(rows: { month: string }[] | undefined, pick: (r: never) => number): Map<string, number> {
  const out = new Map<string, number>();
  for (const row of rows || []) {
    const k = monthKey(row.month);
    out.set(k, (out.get(k) || 0) + (pick(row as never) || 0));
  }
  return out;
}

/** The margin terms Raketech applied to their own workbook. */
interface MarginTerms {
  rate: number;
  /** Last month the margin was taken on partner accounts (`YYYY-MM`). */
  period1End: string;
}

/** Raketech's restated earnings for one account, split at the reconciliation
 *  boundary and put on our net-of-margin basis. */
function restatedForAccount(
  offers: NewReconOffer[],
  lastMonth: string,
  applyMargin: boolean,
  margin: MarginTerms,
): { recon: number; tail: number; monthly: Map<string, number> } {
  let recon = 0;
  let tail = 0;
  const monthly = new Map<string, number>();
  for (const offer of offers) {
    const margined = applyMargin && offer.pa;
    for (const [month, gross] of Object.entries(offer.monthly)) {
      if (month > lastMonth) continue;
      // The margin stopped applying to partner accounts after the first
      // period; Raketech's own Summary reports those months gross.
      const factor = margined && month <= margin.period1End ? 1 - margin.rate : 1;
      const value = gross * factor;
      monthly.set(month, (monthly.get(month) || 0) + value);
      if (month <= RECON_END) recon += value;
      else tail += value;
    }
  }
  return { recon: r2(recon), tail: r2(tail), monthly };
}

function pickBest(candidates: SourceCandidate[]): SourceCandidate {
  let best = candidates[0];
  for (const c of candidates) {
    if (!c.available) continue;
    if (!best.available || c.amount > best.amount) best = c;
  }
  return best;
}

export function buildClaim(
  compare: CompareResponse,
  dataset: NewReconDataset,
  options: V2Options,
): V2Result {
  const { lastMonth, applyMargin } = options;
  const margin: MarginTerms = { rate: dataset.marginRate, period1End: dataset.period1End };

  // Raketech's workbook, grouped by the account id both reports share.
  const restatedByAccount = new Map<string, NewReconOffer[]>();
  for (const offer of dataset.offers) {
    const id = String(offer.hubId || '').trim();
    if (!id) continue;
    if (!restatedByAccount.has(id)) restatedByAccount.set(id, []);
    restatedByAccount.get(id)!.push(offer);
  }

  const reconMonths = dataset.months.filter((m) => m <= RECON_END && m <= lastMonth);
  const tailMonths = dataset.months.filter((m) => m > RECON_END && m <= lastMonth);

  const rows: ClaimRow[] = [];
  const seenAccounts = new Set<string>();

  for (const iss of compare.issues) {
    const accountId = String(iss.accountId || '').trim();
    const offers = accountId ? restatedByAccount.get(accountId) || [] : [];
    if (accountId) seenAccounts.add(accountId);

    const row = buildRow(iss, accountId, offers, options, margin);
    if (row) rows.push(row);
  }

  // Accounts Raketech now reports that the original reconciliation never had.
  // v1 cannot see these at all, so their earnings are pure additions.
  for (const [accountId, offers] of restatedByAccount) {
    if (seenAccounts.has(accountId)) continue;
    const restated = restatedForAccount(offers, lastMonth, applyMargin, margin);
    if (restated.recon === 0 && restated.tail === 0) continue;

    const first = offers[0];
    const candidates: SourceCandidate[] = [{
      source: 'raketech_restated',
      amount: restated.recon,
      marginApplied: applyMargin && offers.some((o) => o.pa),
      available: restated.recon > 0,
    }];
    const earnedTotal = r2(restated.recon + restated.tail);

    rows.push({
      key: `new|${accountId}`,
      accountId,
      acIds: [...new Set(offers.map((o) => String(o.acId)).filter(Boolean))],
      username: '',
      partner: first.partner || '',
      brands: [...new Set(offers.map((o) => o.brand).filter(Boolean))],
      geos: [],
      currency: 'EUR',
      newToThisReport: true,
      missingFromAdwstats: true,
      invoicedRecon: 0,
      invoicedPostRecon: 0,
      invoicedTotal: 0,
      candidates,
      chosenSource: 'raketech_restated',
      earnedReconPeriod: restated.recon,
      earnedTail: restated.tail,
      earnedTailFromAc: 0,
      earnedTotal,
      underpaid: r2(-earnedTotal),
      underpaidPct: earnedTotal > 0 ? -100 : 0,
      attributableToRestatement: earnedTotal,
      offers,
      monthly: buildMonths({
        invoiced: new Map(),
        oldRecon: new Map(),
        synced: new Map(),
        ac: options.acMonthlyByAccountId?.get(accountId) || new Map(),
        restated: restated.monthly,
      }, lastMonth),
      invoiceEntries: [],
    });
  }

  rows.sort((a, b) => a.underpaid - b.underpaid);

  return { rows, summary: summarise(rows, reconMonths, tailMonths) };
}

function buildRow(
  iss: CompareIssue,
  accountId: string,
  offers: NewReconOffer[],
  options: V2Options,
  margin: MarginTerms,
): ClaimRow | null {
  const { lastMonth, applyMargin, acMonthlyByAccountId } = options;

  const invoiced = invoiceByMonth(iss);
  let invoicedRecon = 0;
  let invoicedPostRecon = 0;
  for (const [month, { revenue }] of invoiced) {
    if (month > lastMonth) continue;
    if (month <= RECON_END) invoicedRecon += revenue;
    else invoicedPostRecon += revenue;
  }
  // Fall back to the endpoint's own totals when an issue carries no monthly
  // breakdown, so accounts are never silently valued at zero.
  if (invoiced.size === 0) {
    invoicedRecon = iss.invoiceRevenue || 0;
    invoicedPostRecon = lastMonth > RECON_END ? (iss.postReconInvoiceRevenue || 0) : 0;
  }

  const restated = restatedForAccount(offers, lastMonth, applyMargin, margin);

  const recon = iss.reconciliationEarnings || 0;
  const syncedRaw = iss.adwstatsCommission || 0;
  const manualRaw = iss.manualCheckAmount || 0;

  const candidates: SourceCandidate[] = [
    {
      source: 'reconciliation',
      amount: r2(recon),
      originalAmount: iss.reconciliationEarningsOriginal,
      originalCurrency: iss.currency && iss.currency !== 'EUR' ? iss.currency : undefined,
      marginApplied: false,
      available: recon > 0,
    },
    {
      source: 'synced',
      amount: r2(syncedRaw * 0.95),
      originalAmount: iss.adwstatsCommissionOriginal,
      originalCurrency: iss.adwstatsCurrency && iss.adwstatsCurrency !== 'EUR' ? iss.adwstatsCurrency : undefined,
      marginApplied: true,
      available: syncedRaw > 0,
    },
    {
      source: 'manual_check',
      amount: r2(manualRaw * 0.95),
      originalAmount: iss.partnerCommissionOriginal,
      originalCurrency: iss.currency && iss.currency !== 'EUR' ? iss.currency : undefined,
      marginApplied: true,
      available: manualRaw > 0,
    },
    {
      source: 'raketech_restated',
      amount: restated.recon,
      marginApplied: applyMargin && offers.some((o) => o.pa),
      available: restated.recon > 0,
    },
  ];

  const best = pickBest(candidates);
  const earnedReconPeriod = best.available ? best.amount : 0;

  // Their own workbook is the tail evidence; AC is kept for cross-reference.
  const acMonthly = accountId ? acMonthlyByAccountId?.get(accountId) : undefined;
  const acTail = acTailFor(acMonthly, lastMonth);
  const earnedTail = lastMonth > RECON_END ? Math.max(restated.tail, acTail) : 0;

  const earnedTotal = r2(earnedReconPeriod + earnedTail);
  const invoicedTotal = r2(invoicedRecon + invoicedPostRecon);

  if (earnedTotal <= 0 && invoicedTotal <= 0) return null;

  // What the same account would have looked like without their restatement,
  // which is what makes the restatement's contribution quantifiable.
  const withoutRestated = pickBest(candidates.filter((c) => c.source !== 'raketech_restated'));
  const earnedWithout = r2((withoutRestated.available ? withoutRestated.amount : 0)
    + (lastMonth > RECON_END ? acTail : 0));

  const underpaid = r2(invoicedTotal - earnedTotal);
  const underpaidWithout = r2(invoicedTotal - earnedWithout);

  const invMap = new Map([...invoiced].filter(([m]) => m <= lastMonth));
  const reconMap = seriesByMonth(iss.reconMonthly, (r: { earnings: number }) => r.earnings);
  // Synced months carry the same 5% partner margin as the account-level figure,
  // otherwise the drilldown would not add up to the row above it.
  const syncedMap = seriesByMonth(iss.syncedMonthly, (r: { commission: number }) => r.commission * 0.95);

  return {
    key: accountId ? `cmp|${accountId}` : `cmp|${iss.partner || ''}|${iss.offer || ''}|${iss.geo || ''}`,
    accountId,
    acIds: [...new Set([
      ...(iss.adwstatsAccountId ? [String(iss.adwstatsAccountId)] : []),
      ...offers.map((o) => String(o.acId)).filter(Boolean),
    ])],
    username: iss.username || '',
    partner: iss.partner || '',
    brands: iss.brands && iss.brands.length > 0
      ? iss.brands
      : [...new Set([iss.brand, ...offers.map((o) => o.brand)].filter(Boolean) as string[])],
    geos: iss.geo ? [iss.geo] : [],
    currency: iss.currency || 'EUR',
    newToThisReport: false,
    missingFromAdwstats: !iss.adwstatsAccountId,
    invoicedRecon: r2(invoicedRecon),
    invoicedPostRecon: r2(invoicedPostRecon),
    invoicedTotal,
    candidates,
    chosenSource: best.available ? best.source : 'reconciliation',
    earnedReconPeriod,
    earnedTail: r2(earnedTail),
    earnedTailFromAc: r2(acTail),
    earnedTotal,
    underpaid,
    underpaidPct: earnedTotal > 0 ? r2((underpaid / earnedTotal) * 100) : 0,
    attributableToRestatement: r2(Math.max(0, underpaidWithout - underpaid)),
    offers,
    monthly: buildMonths({
      invoiced: invMap,
      oldRecon: reconMap,
      synced: syncedMap,
      ac: acMonthly || new Map(),
      restated: restated.monthly,
    }, lastMonth),
    invoiceEntries: invoiceEntriesFor(iss, lastMonth),
  };
}

interface MonthSeries {
  invoiced: Map<string, { revenue: number; ftds: number }>;
  oldRecon: Map<string, number>;
  synced: Map<string, number>;
  ac: Map<string, number>;
  restated: Map<string, number>;
}

/**
 * Assembles the month-by-month view of an account.
 *
 * Every series is reported, but only the ones the account-level figure was
 * chosen from decide `earned`: up to the reconciliation boundary that is our
 * own evidence against their restatement, and after it their workbook against
 * Affiliation Cloud. Reporting a series is not the same as claiming on it.
 */
function buildMonths(series: MonthSeries, lastMonth: string): ClaimMonth[] {
  const { invoiced, oldRecon, synced, ac, restated } = series;
  const months = [...new Set([
    ...invoiced.keys(), ...oldRecon.keys(), ...synced.keys(), ...ac.keys(), ...restated.keys(),
  ])]
    .filter((m) => /^\d{4}-\d{2}$/.test(m) && m <= lastMonth)
    .sort();

  return months.map((month) => {
    const inv = invoiced.get(month);
    const invRevenue = r2(inv?.revenue || 0);
    const rest = r2(restated.get(month) || 0);
    const old = r2(oldRecon.get(month) || 0);
    const syn = r2(synced.get(month) || 0);
    const acAmount = r2(ac.get(month) || 0);

    const best = month <= RECON_END
      ? Math.max(old, syn, rest)
      : Math.max(rest, acAmount);

    return {
      month,
      invoiced: invRevenue,
      ftds: inv?.ftds || 0,
      earned: r2(best),
      oldRecon: old,
      synced: syn,
      ac: acAmount,
      restated: rest,
      underpaid: r2(invRevenue - best),
      unsupported: best === 0 && invRevenue > 0,
    };
  });
}

function summarise(
  rows: ClaimRow[],
  reconMonths: string[],
  tailMonths: string[],
): ClaimSummary {
  const bySource = {
    reconciliation: { accounts: 0, earned: 0, underpaid: 0 },
    synced: { accounts: 0, earned: 0, underpaid: 0 },
    manual_check: { accounts: 0, earned: 0, underpaid: 0 },
    raketech_restated: { accounts: 0, earned: 0, underpaid: 0 },
  } as Record<ClaimSource, { accounts: number; earned: number; underpaid: number }>;

  let invoicedTotal = 0, earnedTotal = 0, underpaidTotal = 0, restatementEffect = 0;
  let unverifiableEarnings = 0, unverifiableUnderpaid = 0;
  let accountsNewToReport = 0, accountsMissingFromAdwstats = 0;

  for (const row of rows) {
    invoicedTotal += row.invoicedTotal;
    earnedTotal += row.earnedTotal;
    underpaidTotal += row.underpaid;
    restatementEffect += row.attributableToRestatement;
    if (row.newToThisReport) accountsNewToReport += 1;
    if (row.missingFromAdwstats) {
      accountsMissingFromAdwstats += 1;
      unverifiableEarnings += row.earnedTotal;
      if (row.underpaid < 0) unverifiableUnderpaid += row.underpaid;
    }
    const b = bySource[row.chosenSource];
    b.accounts += 1;
    b.earned += row.earnedTotal;
    b.underpaid += row.underpaid;
  }

  for (const key of Object.keys(bySource) as ClaimSource[]) {
    bySource[key].earned = r2(bySource[key].earned);
    bySource[key].underpaid = r2(bySource[key].underpaid);
  }

  return {
    reconMonths,
    tailMonths,
    accounts: rows.length,
    accountsNewToReport,
    accountsMissingFromAdwstats,
    invoicedTotal: r2(invoicedTotal),
    earnedTotal: r2(earnedTotal),
    underpaidTotal: r2(underpaidTotal),
    underpaidWithoutRestatement: r2(underpaidTotal + restatementEffect),
    restatementEffect: r2(restatementEffect),
    bySource,
    unverifiableEarnings: r2(unverifiableEarnings),
    unverifiableUnderpaid: r2(unverifiableUnderpaid),
  };
}

/**
 * The chain from Raketech's stated position to this report's claim, so the
 * difference between the two bottom lines is itemised rather than asserted.
 */
export function buildClaimBridge(
  dataset: NewReconDataset,
  summary: ClaimSummary,
): ClaimBridgeStep[] {
  const c = dataset.controls;
  const steps: ClaimBridgeStep[] = [];

  steps.push({
    label: 'Underpayment Raketech states in their new report',
    amount: c.underpaymentAfterFees ?? 0,
    kind: 'figure',
    theirFigure: true,
    note: 'Summary sheet, after their own transaction-fee deduction.',
  });

  steps.push({
    label: 'Transaction fees they deduct from what they owe',
    amount: c.transactionFees,
    kind: 'addition',
    note: 'Added back because the fees are disputed: their own Finance ledger does '
      + 'not support the split they state between the two periods.',
  });

  steps.push({
    label: 'Their difference before fees',
    amount: c.difference ?? 0,
    kind: 'subtotal',
    theirFigure: true,
    note: 'Their own figure, and the sum of the two lines above.',
  });

  steps.push({
    label: 'Their headline earnings versus their own per-offer column',
    amount: c.headlineVsPerOfferGap,
    kind: 'figure',
    note: 'They add second-period earnings gross in the headline but margined in the '
      + 'per-offer column. Stated at their higher headline basis, which favours this claim.',
  });

  steps.push({
    label: 'Earnings they report but never invoiced',
    amount: r2(c.statedTotalEarnings - Math.abs(c.totalInvoiced ?? 0)),
    kind: 'figure',
    theirFigure: true,
    note: 'Their stated earnings less their stated invoiced total.',
  });

  steps.push({
    label: 'Invoiced but never paid',
    amount: r2(Math.abs(c.totalInvoiced ?? 0) - (c.totalPaid ?? 0)),
    kind: 'figure',
    theirFigure: true,
    note: 'Their stated invoiced total less their stated paid total.',
  });

  steps.push({
    label: 'This report: shortfall measured per account',
    amount: Math.abs(summary.underpaidTotal),
    kind: 'result',
    note: `Across ${summary.accounts} accounts, valuing each at the strongest evidence `
      + 'available rather than netting one total against another. Account-level netting is '
      + 'what separates this figure from theirs: their bottom line lets overpaid accounts '
      + 'cancel underpaid ones.',
  });

  steps.push({
    label: 'of which exists only because they restated',
    amount: summary.restatementEffect,
    kind: 'figure',
    note: 'Shortfall that appears only when their own new figures are used in place of '
      + 'what we could previously evidence. This part is proved by their own admission.',
  });

  return steps;
}
